import { useGetExamCourseAnalysisReportQuery } from '@report/graphql';
import { OnRowOverlayItemAnalysisTable } from '@report/qbank-item-analysis';
import { Table, TableBody, TableContainer } from '@shared/components';
import { ORDER_TYPE } from '@shared/graphql';
import { TableHeader } from '@shared/table-generator';
import { dayjs } from '@shared/utils';

import { MONTH_ORDER } from '../../constants';
import { useExamCourseAnalysisReportSearchParams } from '../../hooks';
import {
  ExamCourseAnalysisReportTableBody,
  type ExamCourseAnalysisReportTableBodyProps,
} from '../exam-course-analysis-report-table-body';

import { ExamCourseAnalysisReportTableHeader } from './exam-course-analysis-report-table-header';

export default function ExamCourseAnalysisReportTable() {
  const [
    {
      startMonth,
      startYear,
      endMonth,
      endYear,
      examCenter,
      examCourse,
      courseType,
      sortOrder,
      qConfigRef,
    },
  ] = useExamCourseAnalysisReportSearchParams();

  const isSearchEmpty =
    !startMonth ||
    !endMonth ||
    !startYear ||
    !endYear ||
    !examCourse.length ||
    !examCenter.length ||
    !courseType.length ||
    !qConfigRef.length;

  const { data: examCourseAnalysisReportData, loading: isLoading } =
    useGetExamCourseAnalysisReportQuery({
      variables: {
        getExamCourseAnalysisReportInput: {
          filter: {
            startedAt: dayjs()
              .set(
                'month',
                MONTH_ORDER.findIndex((month) => month === startMonth),
              )
              .set('year', startYear)
              .startOf('month')
              .toDate(),
            endedAt: dayjs()
              .set(
                'month',
                MONTH_ORDER.findIndex((month) => month === endMonth),
              )
              .set('year', endYear)
              .endOf('month')
              .toDate(),
            examCenterCodes: examCenter.includes('all') ? null : examCenter,
            examCourseCodes: examCourse.includes('all') ? null : examCourse,
            qConfigRefs: qConfigRef.includes('all') ? null : qConfigRef.map((ref) => Number(ref)),
            examCourseTypes: courseType,
          },
          order: {
            type: sortOrder ? ORDER_TYPE[sortOrder] : ORDER_TYPE.asc,
            field: 'examId',
          },
        },
      },
      skip: isSearchEmpty,
    });

  const data =
    examCourseAnalysisReportData?.reportGetExamCourseAnalysisReport.examCourseAnalysisReports ?? [];
  const rows: ExamCourseAnalysisReportTableBodyProps['rows'] = data.map((record) => ({
    examCode: record.examCourse?.code ?? '-',
    examCourseName: record.examCourse?.name ?? '-',
    type: record.examCourseType,
    qConfigRef: record.qConfigRef,
    totalScore: record.fullScore,
    sdScore: record.standardDeviationScore,
    maxScore: record.maxScore,
    minScore: record.minScore,
    averageScore: record.averageScore,
    examinee: {
      inHouse: record.examineeCount.inHouse ?? null,
      public: record.examineeCount.public ?? null,
      total: record.examineeCount.total ?? null,
    },
    examineeUnique: record.uniqueExamineeCount ?? null,
    avgAttempts: record.avgAttempts ?? null,
    passedExaminee: {
      inHouse: record.passedExamineeCount.inHouse ?? null,
      public: record.passedExamineeCount.public ?? null,
      total: record.passedExamineeCount.total ?? null,
    },
    failedExaminee: {
      inHouse: record.failedExamineeCount.inHouse ?? null,
      public: record.failedExamineeCount.public ?? null,
      total: record.failedExamineeCount.total ?? null,
    },
    passedRate: {
      inHouse: record.passedRate.inHouse ?? null,
      public: record.passedRate.public ?? null,
      total: record.passedRate.total ?? null,
    },
  }));
  const total = data.length;

  return (
    <TableContainer
      sx={{
        borderRadius: (theme) => theme.shape.rounded(2),
        overflowX: 'auto',
        border: '1px solid',
        borderColor: (theme) => theme.palette.grey[300],
      }}
    >
      <Table
        stickyHeader
        size='small'
        sx={{
          border: 'none',
          borderCollapse: 'initial',
          mb: '-1px',
        }}
      >
        <TableHeader
          columns={ExamCourseAnalysisReportTableHeader()}
          slotProps={{
            tableHeader: {
              sx: (theme) => ({
                bgcolor: theme.palette.common.black,
                borderColor: theme.palette.grey[300],
                border: 'none',
                ...theme.typography.body1Semibold,
              }),
            },
            tableHeaderRow: {
              sx: {
                ':first-of-type': {
                  'th:last-of-type': {
                    borderRight: 'none',
                  },
                },
                ':last-of-type > th': {
                  borderTop: '1px solid',
                  borderColor: (theme) => theme.palette.grey[300],
                },
              },
            },
            tableHeaderCell: {
              sx: (theme) => ({
                ...theme.typography.body1Semibold,
                color: theme.palette.common.white,
                backgroundColor: theme.palette.common.black,
                padding: theme.spacing(3),
                borderRight: '1px solid',
                borderBottom: 'none',
                borderColor: theme.palette.grey[300],
              }),
            },
          }}
        />
        {total === 0 ? null : (
          <>
            <TableBody>
              <ExamCourseAnalysisReportTableBody rows={rows} />
            </TableBody>
          </>
        )}{' '}
      </Table>
      <OnRowOverlayItemAnalysisTable
        total={total}
        isLoading={isLoading}
        isFilterSet={!isSearchEmpty}
      />
    </TableContainer>
  );
}
